import { Slider as SliderPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '../../cn';
import { Button } from '../button';

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    trackClassName?: string;
    rangeClassName?: string;
    thumbClassName?: string;
  }
>(
  (
    { className, trackClassName, rangeClassName, thumbClassName, ...props },
    ref,
  ) => (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none select-none items-center',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          'relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20',
          trackClassName,
        )}
      >
        <SliderPrimitive.Range
          className={cn('absolute h-full bg-primary', rangeClassName)}
        />
      </SliderPrimitive.Track>

      <SliderPrimitive.Thumb
        className={cn(
          'block h-4 w-4 rounded-full border border-primary/50 bg-background shadow-sm transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
          thumbClassName,
        )}
      />
    </SliderPrimitive.Root>
  ),
);

Slider.displayName = 'Slider';

export type SliderStyles = {
  rangeClass?: string;
  thumbClass?: string;
  textClass?: string;
};

type LabeledSliderProps<T extends number | string> = {
  ranges: readonly T[];
  value?: T;
  defaultValue?: T;
  step?: number;
  className?: string;
  disabled?: boolean;
  onChange?: (value: T) => void;
  getStyles?: (value?: T) => SliderStyles | undefined;
};

export function LabeledSlider<T extends number | string>({
  ranges,
  defaultValue,
  step = 1,
  className,
  value,
  onChange,
  disabled,
  getStyles,
}: LabeledSliderProps<T>) {
  const maxIndex = ranges.length - 1;
  const valueToIndex = React.useCallback(
    (v?: T) => {
      if (v == null) {
        return undefined;
      }
      const idx = ranges.indexOf(v);
      return idx >= 0 ? idx : undefined;
    },
    [ranges],
  );

  const indexToValue = React.useCallback(
    (i: number) => ranges[Math.min(Math.max(i, 0), maxIndex)],
    [ranges, maxIndex],
  );

  const controlledIndex = valueToIndex(value);
  const defaultIndex = valueToIndex(defaultValue) ?? 0;

  const middleIndex = Math.floor(maxIndex / 2);
  const middleValue = ranges[middleIndex];
  const styles = getStyles?.(value);

  return (
    <div className="grid gap-4 w-full mx-auto">
      <Slider
        disabled={disabled}
        max={maxIndex}
        step={step}
        rangeClassName={styles?.rangeClass}
        thumbClassName={cn(styles?.thumbClass, 'focus:outline-none')}
        className={cn('cursor-pointer bg-neutral-100 rounded-lg', className, {
          'cursor-not-allowed': disabled,
        })}
        {...(controlledIndex !== undefined
          ? { value: [controlledIndex] }
          : { defaultValue: [defaultIndex] })}
        onValueChange={([index]) => {
          onChange?.(indexToValue(index));
        }}
      />

      <div className="mx-[2px] flex items-center justify-between text-xs text-muted-foreground">
        {ranges.map((range, index) => {
          const isActive = range === value;
          const isEdge = index === 0 || index === maxIndex;
          const isMiddle = range === middleValue;

          return (
            <Button
              key={String(range)}
              type="button"
              variant="ghost"
              onClick={() => onChange?.(range)}
              disabled={disabled}
              className={cn(
                'h-fit cursor-pointer p-2',
                isActive && styles?.textClass,
                !isActive && (isEdge || isMiddle) && 'text-neutral-600',
                !isActive && !isEdge && !isMiddle && 'text-neutral-300',
              )}
            >
              {String(range)}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
